/**
 * RemoveMemberModal Component
 * Confirmation dialog for removing (soft-deleting) members
 * Shows warning when member has an active user account
 */

import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { AlertTriangle } from 'lucide-react';
import { ROLE_LABELS } from '../../lib/roles';
import type { Role } from '../../types';

export interface RemoveMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  memberName: string;
  hasLinkedUser: boolean;
  /** Role of that account; an Admin or HR Manager also loses the role (TW-399). */
  linkedUserRole?: Role | null;
  isRemoving?: boolean;
}

export const RemoveMemberModal = ({
  isOpen,
  onClose,
  onConfirm,
  memberName,
  hasLinkedUser,
  linkedUserRole = null,
  isRemoving = false,
}: RemoveMemberModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Remove Member">
      <div className="space-y-4">
        {/* Warning Icon */}
        <div className="flex items-center justify-center">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
            hasLinkedUser ? 'bg-status-warning' : 'bg-status-danger'
          }`}>
            <AlertTriangle className={`h-6 w-6 ${
              hasLinkedUser ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'
            }`} />
          </div>
        </div>

        {/* Member Name */}
        <div className="text-center">
          <p className="text-lg font-medium text-heading mb-2">{memberName}</p>
        </div>

        {/* Warning for active user */}
        {hasLinkedUser && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-sm text-amber-800 dark:text-amber-300 font-medium mb-1">
              This member has a user account
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-400">
              Removing this member will also deactivate their user account. They will no longer be able to log in.
            </p>
            {(linkedUserRole === 'admin' || linkedUserRole === 'hr') && (
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-2">
                Their <b>{ROLE_LABELS[linkedUserRole]}</b> role will be changed to User. If they return, the role has to be granted again.
              </p>
            )}
          </div>
        )}

        {/* Confirmation message */}
        <div className="text-center">
          <p className="text-sm text-body">
            {hasLinkedUser
              ? 'Are you sure you want to remove this member?'
              : 'This will remove the member from the members list and any team they belong to. Are you sure?'}
          </p>
          <p className="text-sm text-meta mt-2">
            You can restore removed members later from the "Removed" filter.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isRemoving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={onConfirm}
            isLoading={isRemoving}
          >
            {hasLinkedUser ? 'Remove & Deactivate' : 'Remove'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
